/**
 * @file Transport links for the app's tRPC client.
 *
 * Most procedures are fast DB reads and share one batched HTTP request. A
 * batch only answers once its slowest procedure finishes, so procedures that
 * wait on AspireX / the legacy services (PFA, STA, backend REST) or a third
 * party go out on their own request instead (EPD-12126). Otherwise e.g. the
 * portfolio preview's payload read (~200ms) rode in one request with
 * `channel.getSocialStats` (seconds) and the page painted nothing until both
 * were done.
 */

import type { AppRouter, RouterInput } from "@repo/api/types";
import {
  httpBatchLink,
  httpLink,
  splitLink,
  type HTTPHeaders,
  type TRPCLink,
} from "@trpc/client";
import superjson from "superjson";

// Whether a dotted procedure/router path exists on the AppRouter, so a renamed
// or removed procedure fails typecheck here instead of silently going back
// into the batch.
export type PathExists<
  T,
  P extends string,
> = P extends `${infer Head}.${infer Rest}`
  ? Head extends keyof T
    ? PathExists<T[Head], Rest>
    : false
  : P extends keyof T
    ? true
    : false;

function appPaths<const P extends readonly string[]>(paths: {
  [I in keyof P]: PathExists<RouterInput, P[I]> extends true ? P[I] : never;
}): readonly string[] {
  return paths;
}

/**
 * Routers whose queries all call AspireX / PFA / STA / legacy services on the
 * response path.
 */
export const UNBATCHED_ROUTERS = appPaths([
  "content",
  "customStages",
  "earnings",
  "marketplace",
  "memberData",
  "products",
  "promoCodes",
  "terms",
]);

/**
 * Legacy-backed procedures in routers that are otherwise DB-only. Keep in sync
 * when a procedure starts (or stops) calling an external system on its
 * response path.
 */
export const UNBATCHED_PROCEDURES = appPaths([
  "channel.getSocialStats",
  "channel.listLegacy",
  "organization.profiles.getLegacyProfile",
  "profile.getHighlightStats",
  "profile.getPublicHighlightStats",
  "profile.getPublicSocialStats",
  "projects.interactions",
  "projects.list",
  "projects.resolveByMemberId",
  "projects.resolveByProgramId",
]);

const unbatchedProcedures = new Set(UNBATCHED_PROCEDURES);

/**
 * True when a procedure path should skip batching: it is listed, or belongs to
 * a router whose procedures all wait on an external system.
 */
export function isUnbatchedPath(path: string): boolean {
  if (unbatchedProcedures.has(path)) return true;
  const router = path.slice(0, path.indexOf("."));
  return UNBATCHED_ROUTERS.includes(router);
}

interface AppTransportOptions {
  url: string;
  headers?: () => HTTPHeaders;
  fetch?: typeof globalThis.fetch;
  /**
   * Fetch credentials; "include" (the session cookie) unless the page runs on
   * the marketing-domain portfolio host, which calls the API without them
   * (EPD-10104, see lib/portfolio-host.ts).
   */
  credentials?: RequestCredentials;
}

/**
 * The terminating transport: slow (external-backed) procedures on their own
 * request via `httpLink`, everything else batched via `httpBatchLink`. Both
 * share the URL, transformer, headers and credentials.
 */
export function createAppTransportLink({
  url,
  headers,
  fetch: fetchImpl,
  credentials = "include",
}: AppTransportOptions): TRPCLink<AppRouter> {
  const options = {
    url,
    transformer: superjson,
    headers,
    // Include credentials for authentication (except from the portfolio
    // host). The global fetch is resolved per call (not captured) so it can be
    // swapped at runtime.
    fetch: (input: RequestInfo | URL, init?: RequestInit) =>
      (fetchImpl ?? globalThis.fetch)(input, {
        ...init,
        credentials,
      }),
  };
  return splitLink({
    condition: (op) => isUnbatchedPath(op.path),
    true: httpLink(options),
    false: httpBatchLink(options),
  });
}
